import React, { useMemo } from "react";
import styled, { css } from "styled-components";
import {
  ArrowDownWideNarrow,
  ArrowUpDown,
  ArrowUpNarrowWide,
  ChevronDown,
} from "lucide-react";
import Dropdown from "common/Dropdown/Dropdown";
import { getNewLookChipColors } from "common/Filters/common/styles.ts";
import {
  NEW_LOOK_PHONE_CONTROL,
  NEW_LOOK_PHONE_HIT,
  phoneMaxWidth,
} from "common/NewLook/layout.ts";
import { LanguageNameSpaces, SortingOrder } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface NewLookSortControlProps<T> {
  sortBy?: T;
  setSortBy: (sortBy: T) => void;
  sortDirection: SortingOrder;
  setSortDirection: (sortDirection: SortingOrder) => void;
  sortValues: T[];
  placeholder?: string;
  labelMapper?: (value: T) => string;
  inChipsRow?: boolean;
}

const ICON_PROPS = { size: 15, strokeWidth: 1.75 } as const;

/** The new look's sort control: one quiet pill with the field and direction. */
const NewLookSortControl = <T extends string>({
  sortBy,
  setSortBy,
  sortDirection,
  setSortDirection,
  sortValues,
  placeholder,
  labelMapper,
  inChipsRow,
}: NewLookSortControlProps<T>) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const getLabel = (value: T) =>
    labelMapper ? labelMapper(value) : (value as unknown as string);

  const menu = useMemo(
    () => ({
      items: sortValues.map((value) => ({
        key: value,
        label: getLabel(value),
      })),
      selectable: true,
      selectedKeys: sortBy ? [sortBy] : [],
      onClick: ({ key }: { key: string }) => setSortBy(key as T),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortValues, sortBy, setSortBy, labelMapper]
  );

  const isAscending = sortDirection === SortingOrder.ASCEND;

  return (
    <Wrapper $inChipsRow={inChipsRow}>
      <Dropdown
        menu={menu}
        trigger={["click"]}
        title={t("labels.sort_by")}
        mobileCloseOnSelect={true}
      >
        <Field type="button" aria-label={t("labels.sort_by")}>
          <ArrowUpDown {...ICON_PROPS} />
          <FieldLabel $isPlaceholder={!sortBy}>
            {sortBy ? getLabel(sortBy) : placeholder ?? t("labels.sort_by")}
          </FieldLabel>
          <ChevronDown size={14} strokeWidth={1.75} />
        </Field>
      </Dropdown>
      <Divider />
      <DirectionButton
        type="button"
        aria-label={t("labels.sort")}
        onClick={() => setSortDirection(-1 * sortDirection)}
      >
        {isAscending ? (
          <ArrowUpNarrowWide {...ICON_PROPS} />
        ) : (
          <ArrowDownWideNarrow {...ICON_PROPS} />
        )}
      </DirectionButton>
    </Wrapper>
  );
};

export default NewLookSortControl;

const Wrapper = styled.div<{ $inChipsRow?: boolean }>`
  display: inline-flex;
  align-items: center;
  height: 32px;
  max-width: 100%;
  border: ${(p) => getNewLookChipColors(p.theme).border};
  border-radius: ${(p) => p.theme.radii.control}px;
  background: ${(p) => getNewLookChipColors(p.theme).background};
  color: ${(p) => p.theme.colors.secondaryText};
  @media (max-width: ${phoneMaxWidth}) {
    height: ${(p) => (p.$inChipsRow ? 32 : NEW_LOOK_PHONE_CONTROL)}px;
  }
`;
const buttonReset = css`
  display: inline-flex;
  align-items: center;
  height: 100%;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  transition: color 150ms, background-color 150ms;
  @media (hover: hover) {
    &:hover {
      color: ${(p) => p.theme.colors.titleColor};
      background: ${(p) => p.theme.colors.cardLightBgColor};
    }
  }
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primaryBlue};
    outline-offset: -2px;
  }
`;
const Field = styled.button`
  ${buttonReset};
  min-width: 0;
  gap: 6px;
  padding: 0 10px 0 12px;
  border-radius: ${(p) => p.theme.radii.control}px 0 0
    ${(p) => p.theme.radii.control}px;
  font-size: 13px;
`;
const FieldLabel = styled.span<{ $isPlaceholder: boolean }>`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(p) =>
    p.$isPlaceholder
      ? p.theme.colors.secondaryText
      : p.theme.colors.titleColor};
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
`;
const Divider = styled.span`
  width: 1px;
  height: 16px;
  background: ${(p) =>
    p.theme.isPhone
      ? p.theme.colors.lightBorderGrey
      : p.theme.colors.cardBorderColor};
`;
const DirectionButton = styled.button`
  ${buttonReset};
  justify-content: center;
  width: 34px;
  @media (max-width: ${phoneMaxWidth}) {
    width: ${NEW_LOOK_PHONE_HIT}px;
  }
  border-radius: 0 ${(p) => p.theme.radii.control}px
    ${(p) => p.theme.radii.control}px 0;
`;
